import { useState, useEffect } from 'react'
import { Plus, Upload, Wand2, Trash2, ChevronDown, ChevronRight, FileText, RefreshCw } from 'lucide-react'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { Label } from '../ui/label'
import { apiClient } from '../../lib/api'
import { BOOK_FILE_FORMATS } from '../../lib/constants'
import { SingleLanguageSelector } from '../shared/SingleLanguageSelector'
import { ALL_LANGUAGES, getLanguageByCode } from '../../lib/constants/languages'

interface Translation {
  id: string
  language: string
  cefr_level: string | null
  file_format: string
  status: string
  is_human_translation: boolean
  created_at: string
}

interface JobStatus {
  id: number
  status: string
  attempts: number
  max_attempts: number
  created_at: string
  updated_at: string
  last_error: string | null
  target_language: string
  level: string
}

interface ErrorLog {
  id: number
  book_id: string
  book_title: string | null
  translation_id: number
  job_id: number
  batch_number: number | null
  target_language: string
  level: string
  raw_text: string
  raw_response: string | null
  error_message: string
  timestamp: string
}

interface TranslationMatrixProps {
  bookId: string
  translations: Translation[]
  onRefresh: () => void
  mode?: 'admin' | 'user'  // defaults to 'admin' for backward compatibility
}

interface PendingAction {
  id: string
  type: 'upload' | 'auto_translate' | 'delete'
  language: string
  cefrLevel: string | null
  file?: File
  translationId?: string
}

const CEFR_LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2', 'Original']

// Helper function to convert language code to name for display
function getLanguageDisplayName(languageValue: string): string {
  // First try to find by code
  const langByCode = getLanguageByCode(languageValue)
  if (langByCode) {
    return langByCode.name
  }
  // If not found by code, assume it's already a name (for backward compatibility)
  return languageValue
}

// Helper function to normalize language value to code
// If it's already a code, return it. If it's a name, try to find the code.
function normalizeLanguageToCode(languageValue: string): string {
  // First check if it's already a code
  const langByCode = getLanguageByCode(languageValue)
  if (langByCode) {
    return langByCode.code
  }
  // Try to find by name
  const langByName = ALL_LANGUAGES.find(lang => lang.name === languageValue)
  if (langByName) {
    return langByName.code
  }
  // If not found, return as-is (might be a code we don't recognize)
  return languageValue
}

export function TranslationMatrix({ bookId, translations, onRefresh, mode = 'admin' }: TranslationMatrixProps) {
  const [expandedLanguages, setExpandedLanguages] = useState<Set<string>>(new Set())
  const [pendingActions, setPendingActions] = useState<PendingAction[]>([])
  const [showAddLanguage, setShowAddLanguage] = useState(false)
  const [newLanguage, setNewLanguage] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [addedLanguages, setAddedLanguages] = useState<string[]>([])
  const [jobStatuses, setJobStatuses] = useState<Record<string, JobStatus>>({})
  const [showErrorLog, setShowErrorLog] = useState(false)
  const [currentErrorLogs, setCurrentErrorLogs] = useState<ErrorLog[]>([])
  const [loadingErrorLogs, setLoadingErrorLogs] = useState(false)
  const [restartingJob, setRestartingJob] = useState<string | null>(null)

  // Fetch job statuses for all translations (admin mode only)
  useEffect(() => {
    if (mode !== 'admin') return
    
    const fetchJobStatuses = async () => {
      const statuses: Record<string, JobStatus> = {}
      
      for (const trans of translations) {
        try {
          const response = await apiClient.getTranslationJobStatus(trans.id)
          if (response.success && response.job) {
            statuses[trans.id] = response.job
          }
        } catch (error) {
          // Job doesn't exist yet (translation not started) - this is normal
          console.debug(`No job found for translation ${trans.id}`)
        }
      }
      
      setJobStatuses(statuses)
    }
    
    fetchJobStatuses()
  }, [translations, mode])

  // Group translations by language and level
  // Normalize language values to codes for internal use
  const translationsByLanguage: Record<string, Record<string, Translation[]>> = {}
  
  translations.forEach(trans => {
    // Normalize language to code for consistent handling
    const languageCode = normalizeLanguageToCode(trans.language)
    if (!translationsByLanguage[languageCode]) {
      translationsByLanguage[languageCode] = {}
    }
    const level = trans.cefr_level || 'Original'
    if (!translationsByLanguage[languageCode][level]) {
      translationsByLanguage[languageCode][level] = []
    }
    translationsByLanguage[languageCode][level].push(trans)
  })

  // Add empty language rows for user-added languages (already codes)
  addedLanguages.forEach(langCode => {
    if (!translationsByLanguage[langCode]) {
      translationsByLanguage[langCode] = {}
    }
  })

  // Sort languages by display name for UI
  const languages = Object.keys(translationsByLanguage).sort((a, b) => {
    const nameA = getLanguageDisplayName(a)
    const nameB = getLanguageDisplayName(b)
    return nameA.localeCompare(nameB)
  })

  const toggleLanguage = (language: string) => {
    const newExpanded = new Set(expandedLanguages)
    if (newExpanded.has(language)) {
      newExpanded.delete(language)
    } else {
      newExpanded.add(language)
    }
    setExpandedLanguages(newExpanded)
  }

  const addLanguageRow = () => {
    if (!newLanguage) {
      alert('Please select a language')
      return
    }
    
    // newLanguage is already a code from SingleLanguageSelector
    if (languages.includes(newLanguage)) {
      alert('This language is already in the list')
      return
    }
    
    // Add language code to the added languages list
    setAddedLanguages([...addedLanguages, newLanguage])
    setExpandedLanguages(new Set([...expandedLanguages, newLanguage]))
    setShowAddLanguage(false)
    setNewLanguage('')
  }

  const addPendingAction = (action: Omit<PendingAction, 'id'>) => {
    setPendingActions([...pendingActions, { ...action, id: Math.random().toString() }])
  }

  const removePendingAction = (actionId: string) => {
    setPendingActions(pendingActions.filter(a => a.id !== actionId))
  }

  const handleFileUpload = (languageCode: string, level: string, file: File) => {
    addPendingAction({
      type: 'upload',
      language: languageCode, // Store code
      cefrLevel: level === 'Original' ? null : level,
      file
    })
  }

  const handleAutoTranslate = (languageCode: string, level: string) => {
    addPendingAction({
      type: 'auto_translate',
      language: languageCode, // Store code
      cefrLevel: level === 'Original' ? null : level
    })
  }

  const handleDeleteTranslation = (translationId: string, languageCode: string, level: string) => {
    addPendingAction({
      type: 'delete',
      language: languageCode, // Store code
      cefrLevel: level === 'Original' ? null : level,
      translationId
    })
  }

  const handleViewErrorLog = async (translationId: string) => {
    setLoadingErrorLogs(true)
    setShowErrorLog(true)
    setCurrentErrorLogs([])
    
    try {
      const response = await apiClient.getTranslationErrorLogs(translationId)
      if (response.success) {
        setCurrentErrorLogs(response.logs)
      }
    } catch (error) {
      console.error('Failed to load error logs:', error)
      alert('Failed to load error logs')
    } finally {
      setLoadingErrorLogs(false)
    }
  }

  const handleRestartJob = async (translationId: string) => {
    if (!confirm('Are you sure you want to restart this translation? A new job will be created.')) {
      return
    }
    
    setRestartingJob(translationId)
    try {
      const response = await apiClient.restartTranslationJob(translationId)
      if (response.success) {
        alert('Translation job restarted successfully! It will be processed shortly.')
        onRefresh()
      }
    } catch (error) {
      console.error('Failed to restart job:', error)
      alert('Failed to restart job. Please try again.')
    } finally {
      setRestartingJob(null)
    }
  }

  const submitActions = async () => {
    if (pendingActions.length === 0) {
      alert('No pending actions to submit')
      return
    }

    setSubmitting(true)
    let successCount = 0
    const errors: string[] = []
    
    try {
      // Process each pending action
      for (const action of pendingActions) {
        try {
          if (action.type === 'upload') {
            // Upload file for translation
            if (!action.file) {
              throw new Error('No file attached to upload action')
            }
            if (mode === 'user') {
              await apiClient.addUserBookTranslation({
                book_id: bookId,
                language: action.language,
                cefr_level: action.cefrLevel,
                file: action.file
              })
            } else {
              await apiClient.addTranslation({
                book_id: bookId,
                language: action.language,
                cefr_level: action.cefrLevel,
                file: action.file
              })
            }
            console.log(`✓ Uploaded translation: ${action.language} - ${action.cefrLevel || 'Original'}`)
            successCount++
          } else if (action.type === 'auto_translate') {
            // Queue auto-translation
            if (!action.cefrLevel) {
              throw new Error('CEFR level required for auto-translation')
            }
            if (mode === 'user') {
              const result = await apiClient.queueUserBookAutoTranslation(bookId, action.language, action.cefrLevel)
              console.log(`✓ Queued auto-translation: ${action.language} - ${action.cefrLevel} using ${result.provider}`)
            } else {
              const result = await apiClient.queueAutoTranslation(bookId, action.language, action.cefrLevel)
              console.log(`✓ Queued auto-translation: ${action.language} - ${action.cefrLevel} using ${result.llm}`)
            }
            successCount++
          } else if (action.type === 'delete') {
            // Delete translation
            if (mode === 'user') {
              await apiClient.deleteUserBookTranslation(bookId, action.translationId!)
            } else {
              await apiClient.deleteTranslation(action.translationId!)
            }
            console.log(`✓ Deleted translation: ${action.translationId}`)
            successCount++
          }
        } catch (error: any) {
          console.error(`Failed to process action:`, error)
          errors.push(`${action.type} ${action.language}-${action.cefrLevel}: ${error.message}`)
        }
      }

      setPendingActions([])
      // Clear added languages that now have translations
      setAddedLanguages([])
      
      // Show appropriate message based on results
      if (errors.length > 0 && successCount > 0) {
        // Some succeeded, some failed
        alert(`${successCount} action(s) completed successfully.\n\n${errors.length} action(s) failed:\n${errors.join('\n')}\n\nCheck console for details.`)
      } else if (errors.length > 0) {
        // All failed
        alert(`All actions failed:\n${errors.join('\n')}\n\nCheck console for details.`)
      } else {
        // All succeeded
        alert('All actions submitted successfully! Auto-translations are queued and will be processed.')
      }
      
      onRefresh()
    } catch (error) {
      console.error('Failed to submit actions:', error)
      alert('Failed to submit actions. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Translation Matrix</CardTitle>
              <CardDescription>
                Manage translations across languages and CEFR levels
              </CardDescription>
            </div>
            <Button onClick={() => setShowAddLanguage(true)} size="sm">
              <Plus className="h-3 w-3 mr-2" />
              Add Language
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {languages.length === 0 ? (
            <div className="text-center py-12 border-2 border-dashed border-border rounded-lg">
              <p className="text-muted-foreground mb-4">No translations yet</p>
              <Button onClick={() => setShowAddLanguage(true)}>
                <Plus className="h-3 w-3 mr-2" />
                Add First Language
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Language Rows */}
              {languages.map(language => (
                <div key={language} className="border border-border rounded-lg overflow-hidden">
                  {/* Language Header */}
                  <div
                    className="flex items-center justify-between p-4 bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() => toggleLanguage(language)}
                  >
                    <div className="flex items-center gap-3">
                      {expandedLanguages.has(language) ? (
                        <ChevronDown className="h-3 w-3 text-muted-foreground" />
                      ) : (
                        <ChevronRight className="h-3 w-3 text-muted-foreground" />
                      )}
                      <h4 className="font-semibold text-foreground">{getLanguageDisplayName(language)}</h4>
                      <Badge variant="secondary">
                        {Object.values(translationsByLanguage[language]).flat().length} translations
                      </Badge>
                    </div>
                    <div className="flex gap-2">
                      {CEFR_LEVELS.map(level => {
                        const hasTranslation = translationsByLanguage[language][level]?.length > 0
                        return (
                          <div
                            key={level}
                            className={`w-12 h-8 rounded flex items-center justify-center text-xs font-medium ${
                              hasTranslation
                                ? 'bg-primary text-primary-foreground'
                                : 'bg-muted text-muted-foreground'
                            }`}
                            title={`${level}${hasTranslation ? ' - Available' : ' - Not available'}`}
                          >
                            {level === 'Original' ? 'Orig' : level}
                          </div>
                        )
                      })}
                    </div>
                  </div>

                  {/* Expanded Content */}
                  {expandedLanguages.has(language) && (
                    <div className="p-4 space-y-3 bg-background">
                      {/* CEFR Level Grid */}
                      <div className="grid grid-cols-7 gap-2">
                        {CEFR_LEVELS.map(level => {
                          const cellTranslations = translationsByLanguage[language][level] || []
                          
                          return (
                            <div key={level} className="space-y-1">
                              <div className="text-xs font-medium text-center text-muted-foreground pb-1 border-b">
                                {level}
                              </div>
                              
                              {/* Existing translations */}
                              {cellTranslations.length > 0 && (
                                <div className="space-y-1">
                                  {cellTranslations.map(trans => {
                                    const jobStatus = jobStatuses[trans.id]
                                    const isFailed = jobStatus?.status === 'failed'
                                    
                                    return (
                                      <div key={trans.id} className="space-y-1">
                                        <div
                                          className={`border rounded p-1.5 text-xs min-h-[32px] flex items-center ${
                                            isFailed 
                                              ? 'border-destructive bg-destructive/10' 
                                              : 'border-border bg-muted/20'
                                          }`}
                                        >
                                          <div className="flex items-center justify-between w-full">
                                            <div className="flex items-center gap-1">
                                              <Badge variant={trans.status === 'available' ? 'default' : 'secondary'} className="text-xs px-1.5 py-0.5 h-5">
                                                {trans.file_format.toUpperCase()}
                                              </Badge>
                                              {trans.is_human_translation && (
                                                <Badge variant="secondary" className="text-xs px-1.5 py-0.5 h-5 bg-blue-100 text-blue-800">
                                                  H
                                                </Badge>
                                              )}
                                            </div>
                                            <button
                                              type="button"
                                              className="p-0.5 hover:bg-destructive/10 rounded h-5 flex items-center justify-center"
                                              onClick={() => handleDeleteTranslation(trans.id, language, level)}
                                              title="Delete"
                                            >
                                              <Trash2 className="h-3 w-3 text-destructive" />
                                            </button>
                                          </div>
                                        </div>
                                        
                                        {/* Failed job actions - shown in admin mode only */}
                                        {isFailed && mode === 'admin' && (
                                          <div className="flex gap-1">
                                            <button
                                              type="button"
                                              onClick={() => handleViewErrorLog(trans.id)}
                                              className="flex-1 flex items-center justify-center gap-1 px-1.5 py-1 text-xs border border-border rounded hover:bg-muted transition-colors"
                                              title="View error log"
                                            >
                                              <FileText className="h-3 w-3" />
                                              <span className="text-[10px]">Log</span>
                                            </button>
                                            <button
                                              type="button"
                                              onClick={() => handleRestartJob(trans.id)}
                                              disabled={restartingJob === trans.id}
                                              className="flex-1 flex items-center justify-center gap-1 px-1.5 py-1 text-xs border border-border rounded hover:bg-primary/10 hover:border-primary transition-colors disabled:opacity-50"
                                              title="Restart translation"
                                            >
                                              <RefreshCw className={`h-3 w-3 ${restartingJob === trans.id ? 'animate-spin' : ''}`} />
                                              <span className="text-[10px]">Retry</span>
                                            </button>
                                          </div>
                                        )}
                                      </div>
                                    )
                                  })}
                                </div>
                              )}
                              
                              {/* Add buttons - always visible */}
                              <div className="flex gap-1 h-[32px]">
                                <div className="flex-1 relative h-full">
                                  <input
                                    type="file"
                                    id={`upload-${language}-${level}`}
                                    accept={BOOK_FILE_FORMATS.acceptString}
                                    className="hidden"
                                    onChange={(e) => {
                                      const file = e.target.files?.[0]
                                      if (file) handleFileUpload(language, level, file)
                                    }}
                                  />
                                  <label
                                    htmlFor={`upload-${language}-${level}`}
                                    className="flex items-center justify-center h-full p-1 border border-dashed border-border rounded hover:border-primary hover:bg-primary/5 cursor-pointer transition-colors"
                                    title={`Upload file (${BOOK_FILE_FORMATS.displayString})`}
                                  >
                                    <Upload className="h-3 w-3 text-muted-foreground" />
                                  </label>
                                </div>
                                {level !== 'Original' && (
                                  <div className="flex-1 h-full">
                                    <button
                                      type="button"
                                      className="w-full h-full flex items-center justify-center p-1 border border-dashed border-border rounded hover:border-primary hover:bg-primary/5 transition-colors"
                                      onClick={() => handleAutoTranslate(language, level)}
                                      title="Auto-translate"
                                    >
                                      <Wand2 className="h-3 w-3 text-muted-foreground" />
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pending Actions Panel */}
      {pendingActions.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Pending Actions</CardTitle>
            <CardDescription>
              Review actions that will be executed when you submit
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 mb-4">
              {pendingActions.map(action => (
                <div
                  key={action.id}
                  className="flex items-center justify-between p-3 border border-border rounded-lg bg-muted/20"
                >
                  <div className="flex items-center gap-3">
                    {action.type === 'upload' && <Upload className="h-3 w-3 text-primary" />}
                    {action.type === 'auto_translate' && <Wand2 className="h-3 w-3 text-purple-500" />}
                    {action.type === 'delete' && <Trash2 className="h-3 w-3 text-destructive" />}
                    <div>
                      <p className="text-sm font-medium">
                        {action.type === 'upload' && 'Upload file'}
                        {action.type === 'auto_translate' && 'Auto-translate'}
                        {action.type === 'delete' && 'Delete translation'}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {getLanguageDisplayName(action.language)} - {action.cefrLevel || 'Original'}
                        {action.file && ` - ${action.file.name}`}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removePendingAction(action.id)}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => setPendingActions([])}
                disabled={submitting}
              >
                Clear All
              </Button>
              <Button onClick={submitActions} disabled={submitting}>
                {submitting ? 'Submitting...' : `Submit ${pendingActions.length} Action${pendingActions.length > 1 ? 's' : ''}`}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add Language Modal */}
      {showAddLanguage && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-background rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Add Language</h3>
            <div className="space-y-4">
              <div>
                <Label htmlFor="new-language">Select Language</Label>
                <div className="mt-1">
                  <SingleLanguageSelector
                    value={newLanguage}
                    onChange={(code) => setNewLanguage(code)}
                    languages={ALL_LANGUAGES.filter(lang => !languages.includes(lang.code))}
                    emptyMessage="All available languages are already added"
                  />
                </div>
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => {
                  setShowAddLanguage(false)
                  setNewLanguage('')
                }}>
                  Cancel
                </Button>
                <Button onClick={addLanguageRow}>
                  Add Language
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error Log Modal */}
      {showErrorLog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-background rounded-lg max-w-4xl w-full max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <h3 className="text-lg font-semibold">Translation Error Log</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowErrorLog(false)}>
                ×
              </Button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6">
              {loadingErrorLogs ? (
                <div className="flex items-center justify-center py-12">
                  <p className="text-muted-foreground">Loading error logs...</p>
                </div>
              ) : currentErrorLogs.length === 0 ? (
                <div className="flex items-center justify-center py-12">
                  <p className="text-muted-foreground">No error logs found</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {currentErrorLogs.map((log) => (
                    <Card key={log.id}>
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div>
                            <CardTitle className="text-base">
                              Batch #{log.batch_number || 'N/A'} - Job #{log.job_id}
                            </CardTitle>
                            <CardDescription>
                              {new Date(log.timestamp).toLocaleString()}
                            </CardDescription>
                          </div>
                          <Badge variant="destructive">
                            {log.target_language} {log.level}
                          </Badge>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div>
                          <h4 className="text-sm font-semibold mb-1">Error Message:</h4>
                          <pre className="text-xs bg-destructive/10 p-3 rounded-lg overflow-x-auto whitespace-pre-wrap">
                            {log.error_message}
                          </pre>
                        </div>
                        
                        {log.raw_text && (
                          <div>
                            <h4 className="text-sm font-semibold mb-1">Input Text (truncated):</h4>
                            <pre className="text-xs bg-muted p-3 rounded-lg overflow-x-auto max-h-32 whitespace-pre-wrap">
                              {log.raw_text.length > 500 
                                ? log.raw_text.substring(0, 500) + '...' 
                                : log.raw_text}
                            </pre>
                          </div>
                        )}
                        
                        {log.raw_response && (
                          <div>
                            <h4 className="text-sm font-semibold mb-1">LLM Response (truncated):</h4>
                            <pre className="text-xs bg-muted p-3 rounded-lg overflow-x-auto max-h-32 whitespace-pre-wrap">
                              {log.raw_response.length > 500 
                                ? log.raw_response.substring(0, 500) + '...' 
                                : log.raw_response}
                            </pre>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
            
            <div className="p-6 border-t border-border">
              <Button onClick={() => setShowErrorLog(false)} className="w-full">
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

